import { useState, useEffect } from 'react';
import { AdminLayout } from '../components/admin/AdminLayout';
import { LogsTable } from '../components/admin/LogsTable';
import { LogDetailsModal } from '../components/admin/LogDetailsModal';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { adminApi, type AdminLog } from '../lib/adminApi';
import {
  Shield,
  Download,
  RefreshCw,
  Filter,
  User,
  Store,
  AlertCircle,
  BarChart3,
} from 'lucide-react';

export default function AdminLogs() {
  const [logs, setLogs] = useState<AdminLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AdminLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AdminLog | null>(null);
  const [currentFilter, setCurrentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { toast } = useToast();

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

 // Cargar logs iniciales
 useEffect(() => {
  fetchLogs(1);
}, []);

  // Recargar cuando cambia itemsPerPage
  useEffect(() => {
    setCurrentPage(1);
    fetchLogs(1);
  }, [itemsPerPage]);

    // Actualizar búsqueda (ahora se haría en la API si tuviéramos endpoint de búsqueda)
  // Por ahora, mantenemos filtrado local para búsqueda
  useEffect(() => {
    // NOTA: Para producción, esto debería hacerse en la API
    // Por ahora, filtramos localmente después de obtener datos paginados
    if (!searchQuery.trim() && currentFilter === 'all') {
      setFilteredLogs(logs);
      return;
    }

    let filtered = [...logs];

    // Aplicar filtro por categoría
    if (currentFilter === 'account') {
      filtered = filtered.filter(log => 
        log.actionType.includes('account') || 
        log.actionType.includes('trial') ||
        log.actionType.includes('shop')
      );
    } else if (currentFilter === 'payment') {
      filtered = filtered.filter(log => 
        log.actionType.includes('payment') || 
        log.actionType.includes('receipt')
      );
    }

    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => {
        const adminName = log.admin?.fullName?.toLowerCase() || '';
        const adminUser = log.admin?.username?.toLowerCase() || '';
        const actionType = log.actionType.toLowerCase();
        const details = JSON.stringify(log.details).toLowerCase();
        
        return adminName.includes(query) || 
               adminUser.includes(query) || 
               actionType.includes(query) ||
               details.includes(query);
      });
    }

    setFilteredLogs(filtered);
  }, [searchQuery, currentFilter, logs]);

  const fetchLogs = async (page: number = 1) => {
    try {
      setLoading(true);
      const result = await adminApi.getLogs({
        page,
        limit: itemsPerPage
      });
      
      setLogs(result.data);
      setFilteredLogs(result.data); // La API ya devuelve datos paginados
      setTotalItems(result.total);
      setCurrentPage(result.page);
      setTotalPages(result.totalPages);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los logs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Resetear a página 1 al buscar
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: string) => {
    setCurrentFilter(filter);
    setSearchQuery(''); // Resetear búsqueda al cambiar filtro
    setCurrentPage(1); // Resetear a página 1
  };

    const handleViewDetails = (log: AdminLog) => {
      setSelectedLog(log);
    setDetailsModalOpen(true);
  };

  const handleExportData = () => {
    toast({
      title: '📊 Datos exportados',
      description: 'Los logs se han exportado exitosamente',
    });
  };

  const handleClearLogs = async () => {
    try {
      // Preguntar confirmación al usuario
      const confirmed = window.confirm(
        '¿Estás seguro de que quieres eliminar los logs antiguos?\n\n' +
        'Se conservarán los logs de los últimos 30 días.\n' +
        'Esta acción no se puede deshacer.'
      );
      
      if (!confirmed) return;
      
      // Mostrar loading
      setLoading(true);
      
      // Llamar a la API
      const result = await adminApi.cleanupOldLogs(30);
      
      toast({
        title: '✅ Logs limpiados',
        description: result.message,
        variant: 'default',
      });
      
      // Recargar logs después de limpiar
      fetchLogs(currentPage);
      
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'No se pudieron limpiar los logs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Calcular estadísticas
  const stats = {
    totalLogs: totalItems, // Usar total de la API
    todayLogs: logs.filter(log => {
      const logDate = new Date(log.createdAt);
      const today = new Date();
      return logDate.toDateString() === today.toDateString();
    }).length,
    adminActions: logs.filter(log => log.admin?.username !== 'system').length,
    systemActions: logs.filter(log => !log.admin || log.admin.username === 'system').length,
    accountActions: logs.filter(log => 
      log.actionType.includes('account') || 
      log.actionType.includes('trial') ||
      log.actionType.includes('shop')
    ).length,
    paymentActions: logs.filter(log => 
      log.actionType.includes('payment') || 
      log.actionType.includes('receipt')
    ).length,
  };

  return (
    <AdminLayout title="Logs de Auditoría">
      {/* Header con estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Logs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalLogs}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-full">
              <Shield className="h-5 w-5 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Hoy</p>
              <p className="text-2xl font-bold text-green-600">{stats.todayLogs}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-full">
              <AlertCircle className="h-5 w-5 text-green-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Acciones Admin</p>
              <p className="text-2xl font-bold text-purple-600">{stats.adminActions}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-full">
              <User className="h-5 w-5 text-purple-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Acciones Sistema</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.systemActions}</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-full">
              <Store className="h-5 w-5 text-yellow-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Acciones y filtros */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Registro de Auditoría</h2>
          <p className="text-gray-600">
            Historial completo de todas las acciones en el sistema
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExportData}>
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>

          <Button variant="outline" onClick={handleClearLogs}>
            <Filter className="h-4 w-4 mr-2" />
            Limpiar logs antiguos
          </Button>

          <Button onClick={() => fetchLogs(currentPage)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tabla de logs */}
      <Card className="p-6">
        <LogsTable
          logs={filteredLogs}
          loading={loading}
          onViewDetails={handleViewDetails}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          currentFilter={currentFilter}
        />

                      {/* Paginación RESPONSIVE */}
        {totalPages > 1 && (
          <div className="mt-6">
            {/* Primera fila: Info y selector en móvil, todo junto en desktop */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              {/* Información de paginación */}
              <div className="text-sm text-gray-600">
                Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> - 
                <span className="font-medium"> {Math.min(currentPage * itemsPerPage, totalItems)}</span> de 
                <span className="font-medium"> {totalItems}</span> logs
              </div>
              
              {/* Selector de items por página - Se mueve abajo en móvil */}
              <div className="sm:self-center">
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm w-full sm:w-auto"
                >
                  <option value="25">25 por página</option>
                  <option value="50">50 por página</option>
                  <option value="100">100 por página</option>
                  <option value="200">200 por página</option>
                </select>
              </div>
            </div>
            
            {/* Segunda fila: Navegación de páginas */}
            <div className="flex flex-col xs:flex-row items-center justify-between gap-3">
              {/* Botón Anterior */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="w-full xs:w-auto order-2 xs:order-1"
              >
                Anterior
              </Button>
              
              {/* Números de página - Se simplifica en móvil */}
              <div className="flex items-center gap-1 order-1 xs:order-2 overflow-x-auto pb-2 xs:pb-0 max-w-full">
                {/* Mostrar solo primeros y últimos números en móvil */}
                <div className="hidden xs:flex space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => fetchLogs(pageNum)}
                        disabled={loading}
                        className="min-w-[40px]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                {/* Versión móvil simplificada */}
                <div className="xs:hidden flex items-center gap-1">
                  <Button
                    variant={currentPage === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => fetchLogs(1)}
                    disabled={loading}
                  >
                    1
                  </Button>
                  
                  {currentPage > 3 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}
                  
                  {currentPage > 2 && currentPage < totalPages - 1 && (
                    <Button
                      variant="default"
                      size="sm"
                      className="min-w-[40px]"
                    >
                      {currentPage}
                    </Button>
                  )}
                  
                  {currentPage < totalPages - 2 && totalPages > 5 && (
                    <span className="px-2">...</span>
                  )}
                  
                  {totalPages > 1 && (
                    <Button
                      variant={currentPage === totalPages ? "default" : "outline"}
                      size="sm"
                      onClick={() => fetchLogs(totalPages)}
                      disabled={loading}
                    >
                      {totalPages}
                    </Button>
                  )}
                </div>
                
                {/* Indicador de página actual en móvil */}
                <div className="xs:hidden text-sm text-gray-600 ml-2 whitespace-nowrap">
                  Pág. {currentPage} de {totalPages}
                </div>
              </div>
              
              {/* Botón Siguiente */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLogs(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="w-full xs:w-auto order-3"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}

      </Card>

      {/* Modal para ver detalles del log */}
      <LogDetailsModal
        log={selectedLog}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
      />
    </AdminLayout>
  );
}